<template>
  <SogPage title="Ausfuhrformulare">
    <SogCard>
      <SogCardActions :is-toolbar="true">
        <SogTextBox
          v-model="_listPage.number"
          placeholder="Nummer"
        />
        <div style="width: 20%">
          <SogDropDownList
            v-model="_listPage.status"
            placeholder="Zustand"
            :data-source="_lstYes"
            :fields="_fields"
            popup-width="auto"
          />
        </div>

        <div style="width: 20%">
          <SogDropDownList
            v-model="_listPage.printType"
            placeholder="Druckstatus"
            :data-source="_listDruck"
            :fields="_fields"
            popup-width="auto"
          />
        </div>
        <SogCardActionButton
          :kind="SogButtonKind.Search"
          style="align-self: flex-end"
          @click="search"
        />
        <SogCardActionButton
          :kind="SogButtonKind.Back"
          style="align-self: flex-end"
        />
      </SogCardActions>
      <SogCardContent>
        <SogGrid
          id="ausfuhrList"
          ref="_grid"
          :url="_url"
          :columns="_columns"
          width="auto"
          :on-command-click="onClick"
          :refresh-on-mount="false"
        />
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import SogGrid from "@/shared/ui/SogGrid.vue";
import {onMounted, reactive, ref, watch} from "vue";
import {Api} from "@/shared/services/api";
import {ColumnModel} from "@syncfusion/ej2-grids/src/grid/models/column";
import {DefaultColumn} from "@/shared/ui/types/SogGridColumnDefinitions";
import SogPage from "@/shared/ui/SogPage.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import {storeToRefs} from "pinia";
import {useInsuranceExportBlankStore} from "@/modules/InsuranceExportBlank/store";
import {useNavigationStore} from "@/modules/Navigation/store";
import {
  InsuranceExportBlankStatus,
  PrintType,
} from "@/shared/model/InsuranceExportBlank";
import {IdType} from "@/shared/model/idType";
import {onBeforeRouteUpdate} from "vue-router";

const _grid = ref<InstanceType<typeof SogGrid> | null>(null);
const _url = Api.baseUrl + "/InsuranceExportBlankList";
const _buttonId = ref("");
const _lstYes = reactive([
  {id: InsuranceExportBlankStatus.Canceled, value: "ungültig"},
  {
    id: InsuranceExportBlankStatus.Archived,
    value: "archiviert",
  },
  {
    id: InsuranceExportBlankStatus.Destroyed,
    value: "vernichtet",
  },
  {
    id: InsuranceExportBlankStatus.IncorrectlyFilled,
    value: "falsch ausgefüllt",
  },
  {id: "all", value: "Alle"},
]);
const _listDruck = reactive([
  {id: PrintType.PrePrinted, value: "Vorgedruckt"},
  {
    id: PrintType.FullPrinted,
    value: "Vollständiggedruckt",
  },
  {
    id: "",
    value: "nicht gedruckt",
  },
  {id: "all", value: "Alle"},
]);
const _fields = {text: "value", value: "id"};
const _insuranceExportBlankStore = useInsuranceExportBlankStore();
const {listPage: _listPage} = storeToRefs(_insuranceExportBlankStore);
const _navigationStore = useNavigationStore();

const _columns: ColumnModel[] = [
  {
    ...DefaultColumn,
    headerText: "",
    visible: true,
    commands: [
      {
        buttonOption: {
          cssClass: "e-flat",
          iconCss: "e-circle-info e-icons",
          click: onClickDetails,
        },
      },
    ],
    textAlign: "Left",
    uid: "details",
    autoFit: true,
    width: "60px",
  },
  {...DefaultColumn, field: "blankNumber", headerText: "Formularnummer"},
  {...DefaultColumn, field: "insuranceNumber", headerText: "VSNR"},
  {
    ...DefaultColumn,
    field: "printTypeDescription",
    headerText: "Druckstatus",
    allowSorting: false,
  },
  {
    ...DefaultColumn,
    field: "statusDescription",
    headerText: "Zustand",
    allowSorting: false,
  },
  {
    ...DefaultColumn,
    field: "manufacturerName",
    headerText: "Versicherer",
    allowSorting: false,
  },
];

async function onClickDetails() {
  _buttonId.value = "details";
}

function search() {
  if (!_grid.value || !_grid.value.adaptor) return;
  _grid.value?.adaptor?.removePredicate("number");
  if (_listPage.value.number) {
    _grid.value?.adaptor.addPredicate(
      "number",
      "contains",
      _listPage.value.number.toUpperCase()
    );
  }

  _grid.value?.adaptor?.removePredicate("status");
  if (_listPage.value.status !== "all") {
    _grid.value?.adaptor.addPredicate(
      "status",
      "equal",
      _listPage.value.status
    );
  }

  _grid.value?.adaptor?.removePredicate("printType");
  if (_listPage.value.printType !== "all") {
    _grid.value?.adaptor.addPredicate("printType", "equal", _listPage.value.printType);
  }
  _grid.value.refresh();
}

onMounted(() => {
  search();
});


onBeforeRouteUpdate(async () => {
  search();
});


watch(_listPage.value, () => {
    search();
});


async function onClick(args: any) {
  _navigationStore.currentOrderNumber = args.rowData.orderNumber;
  if (_buttonId.value === "details") {
    await _navigationStore.showObject(IdType.InsuranceExportBlank, args.rowData.id);
  }
}
</script>

<style scoped></style>
